import { DeleteOutlined, EditOutlined, EyeOutlined, FileExcelOutlined, FilePdfOutlined, MailOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { App, Button, DatePicker, Dropdown, Input, Modal, Select, Space, Tabs, Tag } from "antd";
import { useQuery } from "@tanstack/react-query";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { useEffect, useMemo, useState } from "react";
import type { EntryDraftModuleKey, EntryDraftRecord } from "../../api/draftApi";
import { bulkDeleteBillEntries, deleteBillEntry, getBillEntryPdf, getBulkBillEntryPdf, permanentlyDeleteBillEntry, postPaged } from "../../api/entryApi";
import { confirmDeletePreview } from "../../shared/components/ConfirmDeletePreview";
import { DataGrid } from "../../shared/components/DataGrid";
import { PageHeaderActions } from "../../shared/components/PageHeaderActions";
import { ExcelImportModal } from "../../shared/components/ExcelImportModal";
import { EntryDraftList } from "../../shared/drafts/EntryDraftList";
import { UI_DATE_FORMAT } from "../../shared/utils/date";
import { hasPermission } from "../../shared/utils/permissions";
import { BillEntryWorkbench } from "./BillEntryWorkbench";
import { BulkBillEmailModal } from "./BulkBillEmailModal";
import { BillEmailPreviewModal } from "./BillEmailPreviewModal";
import { InvoicePdfOptionsModal } from "./InvoicePdfOptionsModal";
import { billEntryColumns, buildActiveFilters, deletePreviewFields } from "./billEntryColumns";
import type { BillEntryRow, FilterState, VoucherKind } from "./billEntryTypes";
import { voucherLabel, voucherOptions } from "./billEntryTypes";
import { VoucherEntryDrawer } from "./VoucherEntryDrawer";
import { VoucherEntryWorkbench } from "./VoucherEntryWorkbench";

export function BillEntryPage({ title, voucherType }: { title: string; voucherType?: VoucherKind }) {
  const { message } = App.useApp();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [createVoucherType, setCreateVoucherType] = useState<VoucherKind>(voucherType ?? "SalesInvoice");
  const [emailRow, setEmailRow] = useState<BillEntryRow | null>(null);
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedBillIds, setSelectedBillIds] = useState<number[]>([]);
  const [filters, setFilters] = useState<FilterState>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [activeBillTab, setActiveBillTab] = useState(
    voucherType === "SalesInvoice" || voucherType === "PurchaseInvoice" || voucherType === "ReceiptVoucher" || voucherType === "PaymentVoucher"
      ? "operation"
      : "list"
  );
  const [selectedDraft, setSelectedDraft] = useState<EntryDraftRecord | null>(null);
  const [inlineEditId, setInlineEditId] = useState<number | null>(null);
  const [pdfTarget, setPdfTarget] = useState<{ kind: "single"; id: number } | { kind: "bulk"; ids: number[] } | null>(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  const canCreate = hasPermission("BillEntry.Create");
  const canUpdate = hasPermission("BillEntry.Update");
  const canDelete = hasPermission("BillEntry.Delete");
  const canRead = hasPermission("BillEntry.Read");
  const effectiveVoucherType = voucherType ?? filters.voucherType;
  const showInlineBillScreen = voucherType === "SalesInvoice" || voucherType === "PurchaseInvoice";
  const showInlineVoucherScreen = voucherType === "ReceiptVoucher" || voucherType === "PaymentVoucher";
  const draftModuleKey = isEntryDraftModuleKey(voucherType)
    ? voucherType
    : isEntryDraftModuleKey(createVoucherType)
      ? createVoucherType
      : null;

  useEffect(() => {
    if (showInlineBillScreen || showInlineVoucherScreen) {
      setActiveBillTab("operation");
      return;
    }
    setActiveBillTab("list");
  }, [showInlineBillScreen, showInlineVoucherScreen, voucherType]);

  const payload = useMemo(() => ({
    pageNumber: page,
    pageSize,
    search: filters.search || undefined,
    billNo: filters.billNo || undefined,
    voucherType: effectiveVoucherType,
    isPosted: filters.isPosted,
    fromDate: filters.dateRange?.[0]?.format("YYYY-MM-DD"),
    toDate: filters.dateRange?.[1]?.format("YYYY-MM-DD")
  }), [effectiveVoucherType, filters, page, pageSize]);

  const query = useQuery({
    queryKey: ["bill-entries", payload],
    enabled: canRead,
    queryFn: () => postPaged<BillEntryRow>("/bill-entries/paged", payload)
  });
  const deletedQuery = useQuery({
    queryKey: ["deleted-bill-entries", payload],
    enabled: canRead && activeBillTab === "deleted",
    queryFn: () => postPaged<BillEntryRow>("/bill-entries/deleted/paged", payload)
  });

  const openCreate = (kind: VoucherKind) => {
    setCreateVoucherType(kind);
    setEditId(null);
    setInlineEditId(null);
    setSelectedDraft(null);
    setDrawerOpen(true);
  };

  const openEdit = (row: BillEntryRow) => {
    if (!row.id || !canUpdate) return;
    if (showInlineVoucherScreen) {
      setInlineEditId(row.id);
      setSelectedDraft(null);
      setActiveBillTab("operation");
      return;
    }
    setCreateVoucherType((row.voucherType as VoucherKind) || voucherType || "SalesInvoice");
    setEditId(row.id);
    setSelectedDraft(null);
    setDrawerOpen(true);
  };

  const deleteRow = (row: BillEntryRow) => {
    if (!row.id) return;
    confirmDeletePreview({
      title: "Delete bill entry?",
      entityName: "bill entry",
      fields: deletePreviewFields(row),
      actionDescription: "This will reverse the accounting posting and move the voucher to Deleted Vouchers.",
      okText: "Reverse & Delete",
      onConfirm: async () => {
        await deleteBillEntry(row.id!);
        message.success("Voucher reversed and moved to Deleted Vouchers");
        query.refetch();
        deletedQuery.refetch();
      }
    });
  };

  const deleteSelectedRows = () => {
    if (!selectedBillIds.length || !canDelete) return;
    const count = selectedBillIds.length;
    Modal.confirm({
      title: count === 1 ? "Delete selected voucher?" : `Delete ${count} selected vouchers?`,
      content: "Each posting will be reversed and the voucher will move to Deleted Vouchers. Nothing will be physically removed.",
      okText: "Reverse & Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        if (count === 1) await deleteBillEntry(selectedBillIds[0]);
        else await bulkDeleteBillEntries(selectedBillIds);
        setSelectedBillIds([]);
        message.success(count === 1 ? "Voucher reversed and moved to Deleted Vouchers" : `${count} vouchers reversed and moved to Deleted Vouchers`);
        query.refetch();
        deletedQuery.refetch();
      }
    });
  };

  const permanentlyDeleteRow = (row: BillEntryRow) => {
    if (!row.id || !canDelete) return;
    Modal.confirm({
      title: "Permanently delete this voucher?",
      content: `This permanently removes ${voucherLabel(row.voucherType)} ${row.billNo || row.id} and its stored details. This cannot be undone.`,
      okText: "Permanently Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        await permanentlyDeleteBillEntry(row.id!);
        message.success("Voucher permanently deleted");
        await deletedQuery.refetch();
      }
    });
  };

  const previewPdf = (row: BillEntryRow) => {
    if (!row.id) return;
    setPdfTarget({ kind: "single", id: row.id });
  };

  const previewBulkPdf = () => {
    if (!selectedBillIds.length) return;
    setPdfTarget({ kind: "bulk", ids: [...selectedBillIds] });
  };

  const generateSelectedPdf = async (roundToNearestRupee: boolean) => {
    if (!pdfTarget) return;
    setPdfGenerating(true);
    try {
      const blob = pdfTarget.kind === "single"
        ? await getBillEntryPdf(pdfTarget.id, roundToNearestRupee)
        : await getBulkBillEntryPdf(pdfTarget.ids, roundToNearestRupee);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setPdfTarget(null);
    } catch {
      message.error(pdfTarget.kind === "single" ? "Unable to generate invoice PDF." : "Unable to generate the selected invoice PDF.");
    } finally {
      setPdfGenerating(false);
    }
  };

  const activeFilters = buildActiveFilters(filters, voucherType);
  const listTab = filters.isPosted === true ? "posted" : filters.isPosted === false ? "draft" : "list";
  const pageColumns: ColumnsType<BillEntryRow> = [
    ...billEntryColumns,
    {
      key: "actions",
      title: "",
      width: 118,
      fixed: "right",
      render: (_, row) => row.id ? (
        <Space size={4}>
          <Button size="small" title="View PDF" icon={<EyeOutlined />} onClick={() => previewPdf(row)} />
          <Button size="small" title="Email preview" icon={<MailOutlined />} onClick={() => setEmailRow(row)} />
          {canUpdate ? <Button size="small" title="Edit" icon={<EditOutlined />} onClick={() => openEdit(row)} /> : null}
          {canDelete ? <Button danger size="small" title="Delete" icon={<DeleteOutlined />} onClick={() => deleteRow(row)} /> : null}
        </Space>
      ) : null
    }
  ];

  const pagination: TablePaginationConfig = {
    current: page,
    pageSize,
    total: query.data?.total ?? 0,
    showSizeChanger: true,
    onChange: (nextPage, nextPageSize) => {
      setPage(nextPage);
      setPageSize(nextPageSize);
    }
  };
  const deletedPagination: TablePaginationConfig = {
    ...pagination,
    total: deletedQuery.data?.total ?? 0
  };
  const defaultTabItems = [
    { key: "list", label: "Recent Bills", children: renderBillList() },
    ...(draftModuleKey ? [{
      key: "entry-drafts",
      label: "Saved Drafts",
      children: (
        <EntryDraftList
          moduleKey={draftModuleKey}
          active={activeBillTab === "entry-drafts"}
          onOpen={(draft) => {
            setSelectedDraft(draft);
            setCreateVoucherType(voucherType ?? createVoucherType);
            setEditId(null);
            setDrawerOpen(true);
          }}
        />
      )
    }] : []),
    { key: "deleted", label: "Deleted Vouchers", children: renderDeletedList() }
  ];

  return (
    <div>
      <PageHeaderActions
        title={title}
        subtitle="Bill and voucher list using the existing bill-entry API with tenant/FY headers."
        right={
          <>
            {canCreate ? (
              showInlineBillScreen || showInlineVoucherScreen ? null : voucherType ? (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate(voucherType)}>Create</Button>
              ) : (
                <Dropdown trigger={["click"]} menu={{ items: voucherOptions.map((x) => ({ key: x.value, label: x.label })), onClick: ({ key }) => openCreate(key as VoucherKind) }}>
                  <Button type="primary" icon={<PlusOutlined />}>Create</Button>
                </Dropdown>
              )
            ) : null}
            {voucherType === "SalesInvoice" ? <Button icon={<FileExcelOutlined />} onClick={() => setImportOpen(true)}>Import</Button> : null}
            <Button icon={<ReloadOutlined />} onClick={() => activeBillTab === "deleted" ? deletedQuery.refetch() : query.refetch()}>Refresh</Button>
          </>
        }
      />

      {showInlineBillScreen ? (
        <Tabs
          className="bill-entry-tabs"
          activeKey={activeBillTab}
          onChange={(key) => {
            setActiveBillTab(key);
            setPage(1);
            setSelectedBillIds([]);
            setFilters((current) => ({
              ...current,
              isPosted: key === "posted" ? true : current.isPosted
            }));
          }}
          items={[
            {
              key: "operation",
              label: voucherType === "PurchaseInvoice" ? "Purchase Invoice" : "Sales Invoice",
              children: <BillEntryWorkbench voucherType={voucherType} title={title} draft={selectedDraft} onDraftSaved={() => undefined} onSaved={() => { setSelectedDraft(null); query.refetch(); }} />
            },
            {
              key: "entry-drafts",
              label: "Saved Drafts",
              children: (
                <EntryDraftList
                  moduleKey={voucherType}
                  active={activeBillTab === "entry-drafts"}
                  onOpen={(draft) => {
                    setSelectedDraft(draft);
                    setActiveBillTab("operation");
                  }}
                />
              )
            },
            { key: "posted", label: "Posted Bills", children: renderBillList() },
            { key: "deleted", label: "Deleted", children: renderDeletedList() }
          ]}
        />
      ) : showInlineVoucherScreen ? (
        <Tabs
          className="bill-entry-tabs"
          activeKey={activeBillTab}
          onChange={(key) => {
            setActiveBillTab(key);
            setPage(1);
            setSelectedBillIds([]);
            setFilters((current) => ({
              ...current,
              isPosted: key === "posted" ? true : current.isPosted
            }));
          }}
          items={[
            {
              key: "operation",
              label: title,
              children: (
                <VoucherEntryWorkbench
                  voucherType={voucherType}
                  title={title}
                  draft={selectedDraft}
                  editId={inlineEditId}
                  onDraftSaved={() => undefined}
                  onSaved={() => { setSelectedDraft(null); setInlineEditId(null); query.refetch(); }}
                />
              )
            },
            {
              key: "entry-drafts",
              label: "Saved Drafts",
              children: (
                <EntryDraftList
                  moduleKey={voucherType}
                  active={activeBillTab === "entry-drafts"}
                      onOpen={(draft) => {
                        setSelectedDraft(draft);
                        setInlineEditId(null);
                        setActiveBillTab("operation");
                      }}
                />
              )
            },
            { key: "posted", label: "Posted Bills", children: renderBillList() },
            { key: "deleted", label: "Deleted", children: renderDeletedList() }
          ]}
        />
      ) : (
        <Tabs
          className="bill-entry-tabs"
          activeKey={activeBillTab}
          onChange={(key) => setActiveBillTab(key)}
          items={defaultTabItems}
        />
      )}

      <VoucherEntryDrawer
        open={drawerOpen}
        title={voucherLabel(createVoucherType)}
        voucherType={createVoucherType}
        editId={editId}
        draft={selectedDraft}
        onClose={() => { setDrawerOpen(false); setSelectedDraft(null); }}
        onDraftSaved={() => undefined}
        onSaved={() => { setSelectedDraft(null); query.refetch(); }}
      />

      <BillEmailPreviewModal row={emailRow} onClose={() => setEmailRow(null)} />
      <BulkBillEmailModal billIds={selectedBillIds} open={bulkEmailOpen} onClose={() => setBulkEmailOpen(false)} />
      <ExcelImportModal open={importOpen} module="bill-entry" onClose={() => setImportOpen(false)} onImported={() => query.refetch()} />
      <InvoicePdfOptionsModal
        open={pdfTarget != null}
        invoiceCount={pdfTarget?.kind === "bulk" ? pdfTarget.ids.length : 1}
        loading={pdfGenerating}
        onCancel={() => setPdfTarget(null)}
        onGenerate={generateSelectedPdf}
      />
    </div>
  );

  function renderBillList() {
    return (
      <>
        <div className="bill-filter-bar">
          <Input allowClear prefix={<SearchOutlined />} placeholder="Search party, ledger, remarks" value={filters.search} onChange={(e) => { setPage(1); setFilters((x) => ({ ...x, search: e.target.value || undefined })); }} />
          <Input allowClear placeholder="Bill no" value={filters.billNo} onChange={(e) => { setPage(1); setFilters((x) => ({ ...x, billNo: e.target.value || undefined })); }} />
          {!voucherType ? (
            <Select allowClear placeholder="Voucher type" value={filters.voucherType} options={voucherOptions} onChange={(value) => { setPage(1); setFilters((x) => ({ ...x, voucherType: value })); }} />
          ) : null}
          {!showInlineBillScreen && !showInlineVoucherScreen ? (
            <Select
              allowClear
              placeholder="Posting"
              value={filters.isPosted}
              options={[{ value: true, label: "Posted" }, { value: false, label: "Draft" }]}
              onChange={(value) => { setPage(1); setFilters((x) => ({ ...x, isPosted: value })); }}
            />
          ) : null}
          <DatePicker.RangePicker format={UI_DATE_FORMAT} value={filters.dateRange ?? null} onChange={(value) => { setPage(1); setFilters((x) => ({ ...x, dateRange: value as FilterState["dateRange"] })); }} />
          {voucherType === "SalesInvoice" && activeBillTab === "posted" ? (
            <Button icon={<FilePdfOutlined />} disabled={!selectedBillIds.length} onClick={previewBulkPdf}>
              Generate PDF ({selectedBillIds.length})
            </Button>
          ) : null}
          {canDelete ? (
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={!selectedBillIds.length}
              onClick={deleteSelectedRows}
            >
              Delete{selectedBillIds.length ? ` (${selectedBillIds.length})` : ""}
            </Button>
          ) : null}
          <Button icon={<MailOutlined />} disabled={!selectedBillIds.length} onClick={() => setBulkEmailOpen(true)}>
            Bulk Email ({selectedBillIds.length})
          </Button>
        </div>

        {activeFilters.length ? (
          <div className="active-filter-tags">
            {activeFilters.map((item) => (
              <Tag
                key={item.key}
                closable
                className="filter-chip"
                onClose={(e) => {
                  e.preventDefault();
                  setPage(1);
                  setFilters((current) => ({ ...current, [item.key]: undefined }));
                  if (item.key === "isPosted" && showInlineBillScreen) setActiveBillTab("posted");
                }}
              >
                {item.label}
              </Tag>
            ))}
            <Button size="small" onClick={() => { setPage(1); setFilters(showInlineBillScreen ? { isPosted: true } : {}); if (showInlineBillScreen) setActiveBillTab("posted"); }}>Clear filters</Button>
          </div>
        ) : null}

        <DataGrid
          rows={query.data?.items ?? []}
          columns={pageColumns}
          loading={query.isLoading || query.isFetching}
          pagination={pagination}
          tableId={`bill-entry-${voucherType ?? "all"}-${showInlineBillScreen ? listTab : "list"}`}
          rowSelection={{
            selectedRowKeys: selectedBillIds.map(String),
            getCheckboxProps: (row) => ({ disabled: !row.id }),
            onChange: (_, selectedRows) => setSelectedBillIds(selectedRows.map((row) => row.id).filter((id): id is number => typeof id === "number"))
          }}
          onRowDoubleClick={openEdit}
        />
      </>
    );
  }

  function renderDeletedList() {
    const deletedColumns: ColumnsType<BillEntryRow> = [
      ...billEntryColumns.filter((column) => (column as { dataIndex?: string }).dataIndex !== "isPosted"),
      { title: "Deleted At", dataIndex: "deletedAt", width: 180, render: (value) => value ? new Date(value).toLocaleString("en-IN") : "-" },
      { title: "Deleted By", dataIndex: "deletedBy", width: 190, render: (value) => value || "-" },
      { title: "Accounting", width: 120, render: () => <Tag color="red">Reversed</Tag> },
      {
        key: "actions",
        title: "Actions",
        width: 180,
        fixed: "right",
        render: (_, row) => canDelete ? (
          <Button danger size="small" icon={<DeleteOutlined />} onClick={() => permanentlyDeleteRow(row)}>
            Delete Permanently
          </Button>
        ) : null
      }
    ];

    return (
      <>
        <div className="bill-filter-bar">
          <Input allowClear prefix={<SearchOutlined />} placeholder="Search deleted vouchers" value={filters.search} onChange={(event) => { setPage(1); setFilters((current) => ({ ...current, search: event.target.value || undefined })); }} />
          <Input allowClear placeholder="Bill no" value={filters.billNo} onChange={(event) => { setPage(1); setFilters((current) => ({ ...current, billNo: event.target.value || undefined })); }} />
          {!voucherType ? (
            <Select allowClear placeholder="Voucher type" value={filters.voucherType} options={voucherOptions} onChange={(value) => { setPage(1); setFilters((current) => ({ ...current, voucherType: value })); }} />
          ) : null}
          <DatePicker.RangePicker format={UI_DATE_FORMAT} value={filters.dateRange ?? null} onChange={(value) => { setPage(1); setFilters((current) => ({ ...current, dateRange: value as FilterState["dateRange"] })); }} />
          <Button icon={<ReloadOutlined />} onClick={() => deletedQuery.refetch()}>Refresh Deleted</Button>
        </div>
        <DataGrid
          rows={deletedQuery.data?.items ?? []}
          columns={deletedColumns}
          loading={deletedQuery.isLoading || deletedQuery.isFetching}
          pagination={deletedPagination}
          tableId={`deleted-bill-entry-${voucherType ?? "all"}`}
        />
      </>
    );
  }
}

function isEntryDraftModuleKey(value?: string): value is EntryDraftModuleKey {
  return value === "SalesInvoice"
    || value === "PurchaseInvoice"
    || value === "ReceiptVoucher"
    || value === "PaymentVoucher";
}
